import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  CreditCard,
  MapPin,
  XCircle,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

interface EmployeeProfile {
  _id: string;
  fullName: string;
  email: string;
  alternateEmail?: string;
  phone?: string;
  designation?: string;
  department?: string;
  company: string;
  employeeType: "Employee" | "Intern" | "Part-Time";
  doj?: string;
  dateOfBirth?: string;
  panNumber?: string;
  accountNumber?: string;
  ifsc?: string;
  bankName?: string;
  branch?: string;
  totalPaidLeaves?: number;
  remainingPaidLeaves?: number;
  isActive: boolean;
  resignationDate?: string;
  resignationReason?: string;
  lastWorkingDay?: string;
  createdAt: string;
}

export default function EmployeeProfile() {
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/profile/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setProfile(response.data);
    } catch (error: any) {
      console.error("Error fetching profile:", error);
      setError(error.response?.data?.message || "Failed to fetch profile");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getEmployeeTypeBadge = (type: string) => {
    const badges = {
      Intern: "bg-green-100 text-green-800 border-green-200",
      Employee: "bg-blue-100 text-blue-800 border-blue-200",
      "Part-Time": "bg-purple-100 text-purple-800 border-purple-200",
    };
    return badges[type as keyof typeof badges] || badges.Employee;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your profile...
          </p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">
              {error || "Failed to load profile"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Profile</h1>
          <p className="text-muted-foreground mt-1">
            View your personal and employment information
          </p>
        </div>
        <Badge
          className={
            profile.isActive
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }
        >
          {profile.isActive ? (
            <>
              <CheckCircle className="mr-1 h-3 w-3" />
              Active
            </>
          ) : (
            <>
              <XCircle className="mr-1 h-3 w-3" />
              Inactive
            </>
          )}
        </Badge>
      </div>

      {/* Personal Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <User className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Full Name</p>
                <p className="font-medium mt-1">{profile.fullName}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email Address</p>
                <p className="font-medium mt-1">{profile.email}</p>
              </div>
            </div>

            {profile.alternateEmail && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Alternate Email
                  </p>
                  <p className="font-medium mt-1">{profile.alternateEmail}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Phone className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone Number</p>
                <p className="font-medium mt-1">
                  {profile.phone || "Not provided"}
                </p>
              </div>
            </div>

            {profile.dateOfBirth && (
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium mt-1">
                    {formatDate(profile.dateOfBirth)}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">PAN Number</p>
                <p className="font-medium mt-1">
                  {profile.panNumber || "Not provided"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employment Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            Employment Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Company</p>
                <p className="font-medium mt-1">{profile.company}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Designation</p>
                <p className="font-medium mt-1">
                  {profile.designation || "Not specified"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <MapPin className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Department</p>
                <p className="font-medium mt-1">
                  {profile.department || "Not specified"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <User className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employee Type</p>
                <Badge
                  className={`mt-1 ${getEmployeeTypeBadge(
                    profile.employeeType
                  )}`}
                >
                  {profile.employeeType}
                </Badge>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Calendar className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Date of Joining</p>
                <p className="font-medium mt-1">{formatDate(profile.doj)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leave Balance */}
      {/* <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Leave Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Total Paid Leaves (Annual)
                </p>
                <p className="text-2xl font-bold mt-1">
                  {profile.totalPaidLeaves || 0}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  Remaining Paid Leaves
                </p>
                <p className="text-2xl font-bold mt-1">
                  {profile.remainingPaidLeaves || 0}
                </p>
              </div>
            </div>
          </div>

          {profile.remainingPaidLeaves !== undefined &&
            profile.totalPaidLeaves !== undefined && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Leave Usage</span>
                  <span className="font-medium">
                    {profile.totalPaidLeaves > 0
                      ? (
                          ((profile.totalPaidLeaves -
                            profile.remainingPaidLeaves) /
                            profile.totalPaidLeaves) *
                          100
                        ).toFixed(1)
                      : 0}
                    % used
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full"
                    style={{
                      width: `${
                        profile.totalPaidLeaves > 0
                          ? ((profile.totalPaidLeaves -
                              profile.remainingPaidLeaves) /
                              profile.totalPaidLeaves) *
                            100
                          : 0
                      }%`,
                    }}
                  />
                </div>
              </div>
            )}
        </CardContent>
      </Card> */}

      {/* Bank Details */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Bank Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Account Number</p>
                <p className="font-medium mt-1">
                  {profile.accountNumber
                    ? `XXXX${profile.accountNumber.slice(-4)}`
                    : "Not provided"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bank Name</p>
                <p className="font-medium mt-1">
                  {profile.bankName || "Not provided"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Branch</p>
                <p className="font-medium mt-1">
                  {profile.branch || "Not provided"}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <CreditCard className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">IFSC Code</p>
                <p className="font-medium mt-1">
                  {profile.ifsc || "Not provided"}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resignation Info (if applicable) */}
      {!profile.isActive && profile.resignationDate && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-5 w-5" />
              Resignation Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-red-700">Resignation Date</p>
                <p className="font-medium mt-1 text-red-900">
                  {formatDate(profile.resignationDate)}
                </p>
              </div>

              {profile.lastWorkingDay && (
                <div>
                  <p className="text-sm text-red-700">Last Working Day</p>
                  <p className="font-medium mt-1 text-red-900">
                    {formatDate(profile.lastWorkingDay)}
                  </p>
                </div>
              )}

              {profile.resignationReason && (
                <div className="col-span-full">
                  <p className="text-sm text-red-700">Reason</p>
                  <p className="font-medium mt-1 text-red-900">
                    {profile.resignationReason}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
